import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

/* LOT 53 · remplace la liste grise du navigateur : même API que <select> (value, onChange, <option>, <optgroup>) ; recherche au-delà de 6 choix */
const texte = (c) => React.Children.toArray(c).map((x) => (typeof x === 'string' || typeof x === 'number' ? String(x) : x?.props ? texte(x.props.children) : '')).join('');
const lire = (children, groupe = null, out = []) => {
  React.Children.forEach(children, (c) => {
    if (!c || !c.props) return;
    if (c.type === React.Fragment) lire(c.props.children, groupe, out);
    else if (c.type === 'optgroup') lire(c.props.children, c.props.label, out);
    else if (c.type === 'option') { const l = texte(c.props.children); out.push({ v: String(c.props.value ?? l), l, off: !!c.props.disabled, g: groupe }); }
  });
  return out;
};
const PLEIN = /(^|\s)(w-full|flex-1|grow|block|w-\[100%\])(\s|$)/;

const usePosition = (o, bRef, mRef) => {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!o) return undefined;
    const calc = () => {
      const b = bRef.current; if (!b) return;
      const r = b.getBoundingClientRect(); const dlg = b.closest('[role="dialog"]');
      const h = mRef.current?.offsetHeight || 320; const haut = window.innerHeight - r.bottom < h + 16 && r.top > h + 16;
      const top = haut ? r.top - h - 6 : r.bottom + 6;
      if (dlg) { const d = dlg.getBoundingClientRect(); setPos({ dlg, s: { position: 'absolute', top: top - d.top + dlg.scrollTop - dlg.clientTop, left: r.left - d.left + dlg.scrollLeft - dlg.clientLeft, minWidth: r.width } }); }
      else setPos({ dlg: null, s: { position: 'fixed', top, left: Math.min(r.left, window.innerWidth - 248), minWidth: r.width } });
    };
    calc(); const t = setTimeout(calc, 0);
    window.addEventListener('resize', calc); window.addEventListener('scroll', calc, true);
    return () => { clearTimeout(t); window.removeEventListener('resize', calc); window.removeEventListener('scroll', calc, true); };
  }, [o]); // eslint-disable-line react-hooks/exhaustive-deps
  return pos;
};

export const Select360 = ({ value, defaultValue, onChange, children, className = '', style, disabled, name, required, multiple, 'data-testid': testId, ...rest }) => {
  const [o, setO] = useState(false);
  const [q, setQ] = useState('');
  const [actif, setActif] = useState(-1);
  const [interne, setInterne] = useState(defaultValue != null ? String(defaultValue) : undefined);
  const bRef = useRef(null); const mRef = useRef(null);
  const options = lire(children);
  const val = value !== undefined ? (value == null ? '' : String(value)) : (interne ?? options[0]?.v ?? '');
  const cur = options.find((x) => x.v === val) || (value === undefined || val === '' ? options[0] : null);
  const liste = q ? options.filter((x) => x.l.toLowerCase().includes(q.toLowerCase())) : options;
  const pos = usePosition(o, bRef, mRef);
  useEffect(() => {
    if (!o) return undefined;
    const f = (e) => { if (!bRef.current?.contains(e.target) && !mRef.current?.contains(e.target)) setO(false); };
    const k = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setO(false); bRef.current?.focus(); } };
    document.addEventListener('mousedown', f, true); document.addEventListener('touchstart', f, true); window.addEventListener('keydown', k, true);
    return () => { document.removeEventListener('mousedown', f, true); document.removeEventListener('touchstart', f, true); window.removeEventListener('keydown', k, true); };
  }, [o]);
  const choisir = (x) => {
    setO(false); setQ('');
    if (x.off) return;
    if (value === undefined) setInterne(x.v);
    if (x.v !== val) { const target = { value: x.v, name }; onChange?.({ target, currentTarget: target, preventDefault() {}, stopPropagation() {} }); }
    bRef.current?.focus();
  };
  const clavier = (e) => {
    if (!o && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); setActif(Math.max(0, liste.findIndex((x) => x.v === val))); setO(true); return; }
    if (!o) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActif((i) => Math.min(liste.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActif((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter' && liste[actif]) { e.preventDefault(); choisir(liste[actif]); }
  };
  const plein = PLEIN.test(className) || (style && (style.width || style.flex));
  const menu = o && pos && (
    <div ref={mRef} className="m53 sel53-m sel360-m" role="listbox" style={pos.s} onKeyDown={clavier} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} data-testid={testId ? `${testId}-menu` : undefined}>
      {options.length > 6 && <input className="i53" autoFocus placeholder="Chercher" value={q} onChange={(e) => { setQ(e.target.value); setActif(0); }} style={{ marginBottom: 6 }} data-testid={testId ? `${testId}-chercher` : undefined} />}
      <div className="sel53-l">
        {liste.map((x, i) => <React.Fragment key={`${x.g}-${x.v}-${i}`}>
          {x.g && (i === 0 || liste[i - 1].g !== x.g) && <div className="sel360-g">{x.g}</div>}
          <button type="button" role="option" aria-selected={x.v === val} aria-disabled={x.off} disabled={x.off} className={`${x.v === val ? 'on' : ''}${i === actif ? ' is-actif' : ''}`} onClick={() => choisir(x)} data-testid={testId ? `${testId}-opt-${x.v}` : undefined}>{x.l}</button>
        </React.Fragment>)}
        {!liste.length && <p className="sel360-vide">Aucun résultat</p>}
      </div>
    </div>
  );
  return (
    <span className={`sel53 sel360${plein ? ' is-plein' : ''}`} style={style}>
      <button ref={bRef} type="button" {...rest} className={`b53-s sel53-b sel360-b ${className}`} disabled={disabled} aria-haspopup="listbox" aria-expanded={o} onClick={(e) => { rest.onClick?.(e); setActif(liste.findIndex((x) => x.v === val)); setO(!o); }} onKeyDown={(e) => { rest.onKeyDown?.(e); clavier(e); }} data-testid={testId}>
        <span>{cur ? cur.l : 'Choisir'}</span><ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
      </button>
      {name && !required && <input type="hidden" name={name} value={val} />}
      {required && <input tabIndex={-1} aria-hidden="true" required name={name} value={val} onChange={() => {}} style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none', bottom: 0, left: 0 }} />}
      {menu && createPortal(menu, pos.dlg || document.body)}
    </span>
  );
};

export default Select360;
